import React, { useEffect, useMemo, useState } from 'react';
import {
    collection,
    doc as firestoreDoc,
    getDocs,
    serverTimestamp,
    updateDoc
} from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { db } from '../../firebaseConfig';
import AdminSignOutButton from './AdminSignOutButton';
import './AdminPage.css';

const conversationTypeOptions = [
    'Architecture / Technology Advisory',
    'Speaking / Workshop',
    'Book / Course / Content Collaboration',
    'Resource Request',
    'Education Technology Discussion',
    'Software Delivery / Engineering Partnership',
    'Mentoring / Career Guidance',
    'Other'
];

const statusOptions = ['all', 'new', 'reviewed', 'archived'];

const getCreatedAtDate = (createdAt) => {
    if (!createdAt) return null;
    if (typeof createdAt.toDate === 'function') return createdAt.toDate();
    if (typeof createdAt.toMillis === 'function') return new Date(createdAt.toMillis());
    if (createdAt instanceof Date) return createdAt;
    if (typeof createdAt === 'string' || typeof createdAt === 'number') {
        const parsedDate = new Date(createdAt);
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }
    if (typeof createdAt.seconds === 'number') {
        return new Date(createdAt.seconds * 1000);
    }
    return null;
};

const formatDate = (createdAt) => {
    const date = getCreatedAtDate(createdAt);
    if (!date) return 'Unknown';

    return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short'
    }).format(date);
};

const getCreatedAtTime = (message) => {
    const date = getCreatedAtDate(message.createdAt);
    return date ? date.getTime() : 0;
};

const AdminMessagesPage = ({ user }) => {
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [conversationFilter, setConversationFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortDirection, setSortDirection] = useState('desc');
    const [updatingId, setUpdatingId] = useState('');

    useEffect(() => {
        const fetchMessages = async () => {
            setIsLoading(true);
            setError('');

            try {
                // TODO: Enforce contact read/update/delete access in Firestore Security Rules.
                // Frontend route protection is useful UX, but Firebase rules provide real security.
                const querySnapshot = await getDocs(collection(db, 'contacts'));
                const fetchedMessages = querySnapshot.docs.map((messageDoc) => ({
                    id: messageDoc.id,
                    ...messageDoc.data()
                }));
                setMessages(fetchedMessages);
            } catch {
                setError('Unable to load messages. Check your Firebase rules and admin access.');
            } finally {
                setIsLoading(false);
            }
        };

        if (user) {
            fetchMessages();
        }
    }, [user]);

    const conversationFilters = useMemo(() => {
        const submittedTypes = messages
            .map((message) => message.conversationType)
            .filter(Boolean);
        return ['all', ...Array.from(new Set([...conversationTypeOptions, ...submittedTypes]))];
    }, [messages]);

    const visibleMessages = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();

        return messages
            .filter((message) => {
                const status = message.status || 'new';
                if (statusFilter !== 'all' && status !== statusFilter) return false;
                if (
                    conversationFilter !== 'all' &&
                    message.conversationType !== conversationFilter
                ) {
                    return false;
                }
                if (!normalizedSearch) return true;

                return [message.name, message.email, message.message]
                    .filter(Boolean)
                    .some((value) => String(value).toLowerCase().includes(normalizedSearch));
            })
            .sort((firstMessage, secondMessage) => {
                const firstTime = getCreatedAtTime(firstMessage);
                const secondTime = getCreatedAtTime(secondMessage);
                return sortDirection === 'desc'
                    ? secondTime - firstTime
                    : firstTime - secondTime;
            });
    }, [conversationFilter, messages, searchTerm, sortDirection, statusFilter]);

    const updateMessageStatus = async (messageId, nextStatus) => {
        const timestampField = nextStatus === 'archived' ? 'archivedAt' : 'reviewedAt';
        setUpdatingId(`${messageId}-${nextStatus}`);
        setError('');

        try {
            await updateDoc(firestoreDoc(db, 'contacts', messageId), {
                status: nextStatus,
                [timestampField]: serverTimestamp()
            });

            setMessages((currentMessages) =>
                currentMessages.map((message) =>
                    message.id === messageId
                        ? {
                            ...message,
                            status: nextStatus,
                            [timestampField]: new Date()
                        }
                        : message
                )
            );
        } catch {
            setError('Unable to update message status. Check your Firebase rules.');
        } finally {
            setUpdatingId('');
        }
    };

    return (
        <section className="admin-page">
            <div className="admin-shell">
                <header className="admin-header">
                    <div>
                        <p className="admin-kicker">Private Admin</p>
                        <h1>Messages</h1>
                        <p>
                            Signed in as <strong>{user?.email || 'authenticated user'}</strong>.
                            Contact submissions are loaded from Firebase.
                        </p>
                    </div>
                    <div className="admin-header-actions">
                        <Link className="admin-header-link" to="/admin">
                            Dashboard
                        </Link>
                        <AdminSignOutButton />
                    </div>
                </header>

                <div className="admin-controls" aria-label="Message filters">
                    <label>
                        Status
                        <select
                            value={statusFilter}
                            onChange={(event) => setStatusFilter(event.target.value)}
                        >
                            {statusOptions.map((status) => (
                                <option key={status} value={status}>
                                    {status === 'all' ? 'All' : status}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label>
                        Conversation type
                        <select
                            value={conversationFilter}
                            onChange={(event) => setConversationFilter(event.target.value)}
                        >
                            {conversationFilters.map((type) => (
                                <option key={type} value={type}>
                                    {type === 'all' ? 'All' : type}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label>
                        Sort
                        <select
                            value={sortDirection}
                            onChange={(event) => setSortDirection(event.target.value)}
                        >
                            <option value="desc">Newest first</option>
                            <option value="asc">Oldest first</option>
                        </select>
                    </label>

                    <label className="admin-search">
                        Search
                        <input
                            type="search"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            placeholder="Name, email, or message"
                        />
                    </label>
                </div>

                {error && <p className="admin-error">{error}</p>}

                {isLoading ? (
                    <p className="admin-empty">Loading messages...</p>
                ) : visibleMessages.length === 0 ? (
                    <p className="admin-empty">No messages match the current filters.</p>
                ) : (
                    <div className="admin-message-list">
                        {visibleMessages.map((message) => {
                            const status = message.status || 'new';
                            return (
                                <article className="admin-message-card" key={message.id}>
                                    <div className="admin-message-topline">
                                        <div>
                                            <h2>{message.name || 'Unnamed contact'}</h2>
                                            <a href={`mailto:${message.email}`}>{message.email || 'No email'}</a>
                                        </div>
                                        <span className={`admin-status admin-status-${status}`}>
                                            {status}
                                        </span>
                                    </div>

                                    <dl className="admin-message-meta">
                                        <div>
                                            <dt>Organization / role</dt>
                                            <dd>{message.organizationRole || 'Not provided'}</dd>
                                        </div>
                                        <div>
                                            <dt>Country</dt>
                                            <dd>{message.country || 'Not provided'}</dd>
                                        </div>
                                        <div>
                                            <dt>Phone</dt>
                                            <dd>{message.phone || 'Not provided'}</dd>
                                        </div>
                                        <div>
                                            <dt>Conversation type</dt>
                                            <dd>{message.conversationType || 'Not provided'}</dd>
                                        </div>
                                        <div>
                                            <dt>Preferred contact</dt>
                                            <dd>{message.preferredContactMethod || 'Not provided'}</dd>
                                        </div>
                                        <div>
                                            <dt>Created</dt>
                                            <dd>{formatDate(message.createdAt)}</dd>
                                        </div>
                                    </dl>

                                    <div className="admin-message-body">
                                        <h3>Message</h3>
                                        <p>{message.message || 'No message provided.'}</p>
                                    </div>

                                    <div className="admin-message-actions">
                                        <button
                                            type="button"
                                            onClick={() => updateMessageStatus(message.id, 'reviewed')}
                                            disabled={
                                                status === 'reviewed' ||
                                                updatingId === `${message.id}-reviewed`
                                            }
                                        >
                                            Mark reviewed
                                        </button>
                                        <button
                                            type="button"
                                            className="admin-secondary-button"
                                            onClick={() => updateMessageStatus(message.id, 'archived')}
                                            disabled={
                                                status === 'archived' ||
                                                updatingId === `${message.id}-archived`
                                            }
                                        >
                                            Archive
                                        </button>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}
            </div>
        </section>
    );
};

export default AdminMessagesPage;
